import { defineStore } from "pinia";
import { ref } from "vue";
import type { LobbyResp, Profile } from "@/server-types";
import * as http from "@/tools/http";
import { useAuthStore } from "./auth";
import { useLobbiesStore } from "./lobbies";
import { useToastStore } from "./toast";

export const useLobbyStore = defineStore("lobby", () => {
	const lobbyID = ref(-1);
	const teammate = ref<Profile>();
	const opponent1 = ref<Profile>();
	const opponent2 = ref<Profile>();
	const isAdmin = ref(false);

	const auth = useAuthStore();
	const lobbies = useLobbiesStore();
	const toast = useToastStore();

	const invitingFor = ref<"teammate" | "opponent">();
	const invitations = ref<number[]>([]);

	function receiveInvitation(lobbyId: number) {
		invitations.value.push(lobbyId);
		toast.success("You have a new lobby invitation");
	}

	function removeInvitation(lobbyId: number) {
		const index = invitations.value.indexOf(lobbyId);
		if (index > -1) {
			invitations.value.splice(index, 1);
		}
	}

	function clearInvitations() {
		invitations.value.length = 0;
	}


    let refreshVersion = 0;
    async function update() {
        const version = ++refreshVersion;
        const id = await http.getMyLobbyID();
        if (version !== refreshVersion) return;
        if (typeof id !== "number" || id < 0) {
            clearLobby();
            return;
        }
        const snapshot: LobbyResp = await http.getLobbyByID(id);
        const username = auth.username;
        const {teamA, teamB} = snapshot.teams;
        const myTeam = teamA.includes(username) ? teamA : teamB;
        const otherTeam = teamA.includes(username) ? teamB : teamA;
        const names = [myTeam.find(name => name && name !== username), otherTeam[0], otherTeam[1]];
        const profiles = await Promise.all(names.map(name => name ? http.getProfile(name) : undefined));
        if (version !== refreshVersion) return;
        lobbyID.value = id;
        isAdmin.value = username === teamA[0];
        [teammate.value, opponent1.value, opponent2.value] = profiles;
    }

	async function createLobby() {
		await http.createLobby();
		await update();
	}

	async function addBot(teamId: "A" | "B", variant?: string) {
		await http.addBot(lobbyID.value, teamId, variant);
		await update();
	}

	async function removePlayer(username: string) {
		await http.removeLobbyPlayer(lobbyID.value, username);
		await update();
	}

	/** What the server offers when a seat is given to a computer; empty means no choice. */
	async function botVariants(): Promise<http.BotVariant[]> {
		try {
			return await http.getBotVariants();
		} catch {
			return [];
		}
	}

	async function startLobby(points: number) {
		if (lobbyID.value > -1) {
			await http.startLobby(lobbyID.value, points);
			clearLobby();
			lobbies.onLobbyDeleted(lobbyID.value);
		}
	}

	async function deleteLobby() {
		if (lobbyID.value > -1) {
			await http.deleteLobby(lobbyID.value);
			clearLobby();
		}
	}

	async function leaveLobby() {
		if (lobbyID.value > -1) {
			await http.leaveLobby(lobbyID.value);
			clearLobby();
		}
	}

	function clearLobby() {
        refreshVersion++;
        isAdmin.value = false;
		lobbyID.value = -1;
		teammate.value = undefined;
		opponent1.value = undefined;
		opponent2.value = undefined;
		invitingFor.value = undefined;
	}

	async function veryQuickGame(points = 38) {
		await createLobby();
		await addBot("B");
		await addBot("B");
		await addBot("A");
		await startLobby(points);
	}

	update();

	return {
		addBot,
		removePlayer,
		botVariants,
		teammate,
		opponent1,
		opponent2,
		lobbyID,
		isAdmin,
		createLobby,
		deleteLobby,
		startLobby,
		update,
		invitingFor,
		receiveInvitation,
		clearInvitations,
		invitations,
		clearLobby,
		removeInvitation,
		veryQuickGame,
		leaveLobby
	};
});
